import { IPeriod, IPeriodLevel } from "backend/model/model";
import * as conf from "qpr-base/configuration";
import { KPIValues } from "../KPIsForm/index.dto";
import { KPI_JS_ATTR, WorkflowData } from "./dataMapperTypes";

export const mapKpiToWorkflow = (
  data: KPIValues,
  periodlevels: IPeriodLevel[],
  meaId?: string
): WorkflowData => {
  data = data as KPIValues;

  const lang: string[] = conf.getLanguages();

  const JS_ATTR: KPI_JS_ATTR = {
    "Localized-Text-Attributes": {
      [lang[0]]: {
        ...data.basicInformation[lang[0]],
      },
      [lang[1]]: {
        ...data.basicInformation[lang[1]],
      },
    },
  };
  const JS_ATTR_STRINGIFIED = JSON.stringify(JS_ATTR);

  const operationType = meaId ? "modify" : "create";

  const getFirstPeriod = () => {
    const level = periodlevels.find((level) => level.id === data.periodlevelid);
    if (!level) return "undefined";
    return level.periods[0].name;
  };

  const getPeriods = (): IPeriod[] => {
    const level = periodlevels.find((level) => level.id === data.periodlevelid);
    if (!level) return [];
    return level.periods;
  };
  const periods = getPeriods();

  const values: {
    series: string;
    period?: string | undefined;
    value: string;
  }[] = [];
  for (let i = 0; i < data.values.length; i += 1) {
    if (data.values[i]) {
      for (const [key, value] of Object.entries(data.values[i])) {
        if (key !== "JS_ATTR") {
          values.push({
            series: key,
            period: periods[i].name,
            value:
              typeof value === "number" ? value.toString() : (value as string),
          });
        }
      }
    }
  }

  return {
    elementType: "KPI",
    type: operationType,
    meaId:
      (operationType === "create" ? data.parentelementid : data.meaId) || "",
    parameters: {
      elements: [
        {
          elementType: "KPI",
          parentsForReferences: [],
          operation: operationType,
          meaId:
            (operationType === "create" ? data.parentelementid : data.meaId) ||
            "",
          attributes: [
            ["name", data.basicInformation[lang[0]].Name || "-"],
            ["description", data.basicInformation[lang[0]].Description || ""],
            ["valuesettingid", data.valuesettingid || "error"],
            ["periodlevelid", data.periodlevelid || "error"],
            ["measureunitid", data.measureunitid || "error"],
            ["parentelementid", data.parentelementid],
          ].filter((e: any) => e[1] !== "error"),
          values: [
            {
              series: "JS_ATTR",
              period: getFirstPeriod(),
              value: JS_ATTR_STRINGIFIED,
            },
            ...values.filter(
              (e: any) =>
                operationType === "modify" ||
                (e.value !== "" && e.value !== null)
            ),
          ],
          subElements: [],
        },
      ],
    },
  };
};

export const mapWorkflowToKPIValues = (
  data: WorkflowData,
  periodLevels: IPeriodLevel[]
): KPIValues => {
  const unparsedData = data.parameters.elements[0].values.find(
    (cur) => cur.series === "JS_ATTR"
  )?.value;
  const workflow = unparsedData
    ? (JSON.parse(unparsedData) as KPI_JS_ATTR)
    : ({} as KPI_JS_ATTR);
  const values = data.parameters.elements[0].values || [];

  let referenceFrom: any = data.parameters.elements[0].subElements?.filter(
    (obj) => "referenceFrom" in obj
  )[0];
  referenceFrom = referenceFrom?.referenceFrom || "";

  const unit =
    data.parameters.elements[0].attributes.find(
      (e: any) => e[0] === 'unit(series="ACT")'
    ) ||
    data.parameters.elements[0].attributes.find(
      (e: any) => e[0] === "measureunitid"
    );
  const position = data.parameters.elements[0].attributes.find(
    (e: any) => e[0] === "valuesettingid"
  );

  const periodlevel = data.parameters.elements[0].attributes.find(
    (e: any) => e[0] === "periodlevelid"
  );

  const parentelementid = data.parameters.elements[0].attributes.find(
    (e: any) => e[0] === "parentelementid"
  );

  let topelementid;
  let parentElement: any = undefined;
  let prioritySymbol = "";
  let perspectiveSymbol = "";
  if (parentelementid?.length) {
    parentElement = data.parentHierarchy?.find(
      (element: any) => element.id === parentelementid[1]
    );
    if (parentElement) topelementid = parentElement.topelementid;
  }

  if (parentElement?.typename === "Perspective")
    perspectiveSymbol = parentElement.symbol;
  if (parentElement?.typename === "Strategic Objective") {
    prioritySymbol = parentElement.symbol;
    perspectiveSymbol =
      data.parentHierarchy?.find(
        (element: any) => element.id === parentElement.parentelementid
      )?.symbol || "";
  }

  const lang: string[] = conf.getLanguages();

  const periods =
    periodLevels?.find(
      (level: IPeriodLevel) => level.id === (periodlevel?.[1] || "")
    )?.periods || [];

  const mappedValues: KPIValues["values"] = [];
  for (let i = 0; i < periods.length; i += 1) {
    const periodsValues = values.filter(
      (val: any) => val.period === periods[i].name
    );
    if (periodsValues.length === 0) mappedValues.push(null);
    else {
      const valueObj: any = {};
      for (let j = 0; j < periodsValues.length; j += 1) {
        valueObj[`${periodsValues[j].series}`] = periodsValues[j].value;
      }
      mappedValues.push(valueObj);
    }
  }

  const mappeddata = {
    meaId: data.parameters.elements[0].meaId || "",
    basicInformation: {
      [lang[0]]: {
        Description:
          workflow["Localized-Text-Attributes"]?.[lang[0]]?.Description || "",
        Name: workflow["Localized-Text-Attributes"]?.[lang[0]]?.Name || "",
      },
      [lang[1]]: {
        Description:
          workflow["Localized-Text-Attributes"]?.[lang[1]]?.Description || "",
        Name: workflow["Localized-Text-Attributes"]?.[lang[1]]?.Name || "",
      },
    },
    values: mappedValues,
    measureunitid: unit?.[1] || "",
    parentelementid: parentelementid?.[1] || "",
    valuesettingid: position && position.length > 1 ? position[1] : "",
    periodlevelid: periodlevel && periodlevel.length > 1 ? periodlevel[1] : "",
    location: {
      topelementid: topelementid || "",
      themesymbol: perspectiveSymbol,
      prioritysymbol: prioritySymbol,
    },
  };
  return mappeddata;
};
